import { generateUUID } from '@/lib/uuid.js';

/*
------------------------------------------------------------------------
Unit conversion constants
------------------------------------------------------------------------
*/
export const METERS_TO_FEET = 3.28084;
export const FEET_TO_METERS = 0.3048;

/*
------------------------------------------------------------------------
Step status flags
------------------------------------------------------------------------
*/
export const statusFlags = {
    CURRENT: 'current',
    UPCOMING: 'upcoming',
    COMPLETE: 'complete',
};

//
// Define the steps from which the step navigation will be rendered,
// and so we can reuse these names elsewhere in the site.
// Creates a structure like this in the store:
// steps: [
//    { name: 'Start', status: statusFlags.CURRENT },
//    ...
// ],
//
export const stepNames = ['Start', 'Project', 'Room', 'Product', 'Report'];
const stepDefinitions = [];
stepNames.forEach((step) => {
    if (step === 'Start') {
        stepDefinitions.push({ name: step, status: statusFlags.CURRENT, valid: false });
    } else {
        stepDefinitions.push({ name: step, status: statusFlags.UPCOMING, valid: false });
    }
});

/*
------------------------------------------------------------------------
DEFAULT STATE
------------------------------------------------------------------------
*/
export const defaultState = {
    /**
     * The version is here to be used as a hook to know if the structure of the
     * state data in localstorage has changed and needs to be handled when the
     * user comes back to the site so it does not cause errors because code is
     * looking for an old strcture. For example, when the structre of the user data
     * changes, or if the definiton of the steps changes, etc.
     */
    version: 1.6,
    instance_id: '',
    statusFlags: { ...statusFlags },
    stepNames: [...stepNames],
    steps: [...stepDefinitions],
    // Room baseline calculation (room without any products)
    roomBaseline: {},
    // Combined calculation result (all products together)
    combinedCalculation: {},
    user: {
        reset: true,
        currentStepName: 'Start',
        currentStepIndex: 0,
        firstname: '',
        lastname: '',
        email: '',
        company: '',
        projectname: '',
        roomname: '',
        room: {
            width: 0,
            length: 0,
            height: 0,
            unit: 'imperial',
            materials: [],
            usage: '',
            usagetarget: '',
        },
        products: [],
        activeProductId: null, // UUID of the currently active/selected product
        numproducts: 1,
    },
    validation: {
        Start: {
            valid: false,
            showErrors: false,
            errors: {},
        },
        Project: {
            valid: false,
            showErrors: false,
            errors: {},
        },
        Room: {
            valid: false,
            showErrors: false,
            errors: {},
        },
        Product: {
            valid: false,
            showErrors: false,
            errors: {},
        },
        Report: {
            valid: false,
            showErrors: false,
            errors: {},
        },
    },
    showFormLabels: true,
    overlays: {
        product: {
            open: false,
            productId: '',
            message: '',
            data: null,
            extendedData: [],
        },
        productvalidation: {
            open: false,
            message: '',
        },
        reporterror: {
            open: false,
            message: '',
        },
    },
    products: [],
    productsExtended: {},
    surfaceNrc: [
        { surface: 'Walls', identifier: 'walls_default', nrc: '0.05', label: '' },
        { surface: 'Ceiling', identifier: 'ceiling_default', nrc: '0.15', label: '' },
        { surface: 'Ceiling', identifier: 'suspended_ceiling_tiles', nrc: '0.5', label: 'Suspended Ceiling Tiles' },
        { surface: 'Floor', identifier: 'floor_default', nrc: '0.05', label: '' },
        { surface: 'Floor', identifier: 'floor_carpet', nrc: '0.25', label: 'Floor Carpet' },
    ],
    loading: false,
    loading_message_default: 'Loading.',
    loading_message: 'Loading.',
    triggerDocGen: false,
    isCalculating: false, // Global flag to prevent recursive calculations
    canvasNeedsRedraw: false, // Flag to trigger canvas redraw after calculation completes
    syncRoomData: false,
    svgTemp: '',
    showRoomForm: false,
};

/*
------------------------------------------------------------------------
STATE INITIALIZATION FUNCTION
Handles localStorage loading and data migrations
------------------------------------------------------------------------
*/
export function createState() {
    // Load only `user` data from localStorage, or fall back to default
    const savedUserState = localStorage.getItem('luxxbox_acoustic_analyzer_user');
    let initialUser = savedUserState ? JSON.parse(savedUserState) : defaultState.user;

    // Migration: Convert old flat structure to new nested structure
    if (initialUser && initialUser.roomwidth !== undefined && !initialUser.room) {
        // Old structure detected (flat room properties at top level)
        initialUser = {
            ...initialUser,
            roomname: initialUser.roomname || '',
            room: {
                width: initialUser.roomwidth || 0,
                length: initialUser.roomlength || 0,
                height: initialUser.roomheight || 0,
                unit: initialUser.roomunit || 'imperial',
                materials: initialUser.roommaterials || [],
                usage: initialUser.roomusage || '',
                usagetarget: initialUser.roomusagetarget || '',
            },
        };
        // Remove old flat properties
        delete initialUser.roomwidth;
        delete initialUser.roomlength;
        delete initialUser.roomheight;
        delete initialUser.roomunit;
        delete initialUser.roommaterials;
        delete initialUser.roomusage;
        delete initialUser.roomusagetarget;
    }

    // Migration: Convert old flat product structure to products array
    if (initialUser && initialUser.product !== undefined && !initialUser.products) {
        // Create products array with single product if one was selected
        const hasProduct = initialUser.product !== '';
        if (hasProduct) {
            const productId = generateUUID();
            initialUser.products = [
                {
                    id: productId,
                    product: initialUser.product || '',
                    shape: '', // Shape selection for products with shape variants
                    size: initialUser.productsize || '',
                    size_variant: initialUser.productsizevariant || '',
                    metric_sabins: initialUser.productsize_metric_sabins || '',
                    data: initialUser.productdata || {},
                    dataExtended: initialUser.productdataExtended || [],
                    manualUnits: null,
                    calculation: null, // Will be populated by batch API
                },
            ];
            // Set migrated product as active
            initialUser.activeProductId = productId;
        } else {
            initialUser.products = [];
            initialUser.activeProductId = null;
        }

        // Remove old flat properties
        delete initialUser.product;
        delete initialUser.productsize;
        delete initialUser.productsizevariant;
        delete initialUser.productsize_metric_sabins;
        delete initialUser.productsize_lumen_d;
        delete initialUser.productsize_lumen_i;
        delete initialUser.productsize_lumen_total;
        delete initialUser.productsize_output;
        delete initialUser.productdata;
        delete initialUser.productdataExtended;
    }

    // Migration: Set activeProductId if it doesn't exist but products array does
    if (initialUser && initialUser.products && initialUser.products.length > 0 && !initialUser.activeProductId) {
        // Set first product as active (maintains backward compatibility)
        initialUser.activeProductId = initialUser.products[0].id;
    }

    // Migration: Add calculation property to existing products that don't have it
    if (initialUser && initialUser.products && initialUser.products.length > 0) {
        initialUser.products = initialUser.products.map((product) => {
            if (product.calculation === undefined) {
                return { ...product, calculation: null };
            }
            return product;
        });
    }

    // Migration: Add shape property to existing products that don't have it
    if (initialUser && initialUser.products && initialUser.products.length > 0) {
        initialUser.products = initialUser.products.map((product) => {
            if (product.shape === undefined) {
                return { ...product, shape: '' };
            }
            return product;
        });
    }

    return {
        ...defaultState,
        user: initialUser,
    };
}
